/**
 * Who to call next, ranked by the API: a lead inside its first five minutes, then callbacks that
 * are late, then callbacks coming due, then new leads freshest first, then leads worth another
 * try. One merged list, so an agent never weighs a lead list against a task list. The first row
 * carries the page's one primary button.
 */
import type { Cockpit, UpNextItem, UpNextReason } from "@tlcrm/shared";
import { Link } from "react-router";

import { EligibilityFlags, RecordMark } from "../../components/kit";
import { mayDial } from "../../lib/cockpit";
import { Icon } from "../../components/Icon";
import { dial } from "../../lib/dial";
import { age, when } from "../../lib/format";

const REASON: Record<UpNextReason, string> = {
  fresh_lead: "Fresh lead",
  overdue_callback: "Callback late",
  callback_soon: "Callback due",
  new_lead: "New lead",
  working_lead: "Try again",
};

function href(i: UpNextItem): string {
  if (i.leadId) return `/leads/${i.leadId}`;
  if (i.contactId) return `/contacts/${i.contactId}`;
  return "/tasks";
}

function Timing({ item, now }: { item: UpNextItem; now: number }) {
  const at = new Date(item.at).getTime();
  if (item.taskId) {
    const late = now - at;
    return late > 0 ? (
      <span className="mark mark-brick" title={`Promised for ${when(item.at)}`}>
        {age(late)} late
      </span>
    ) : (
      <span className="upnext-age mono" title="When it was promised">
        {when(item.at)}
      </span>
    );
  }
  const waited = now - at;
  return (
    <span
      className={`upnext-age mono${waited < 5 * 60_000 ? " is-fresh" : waited > 30 * 60_000 ? " is-cold" : ""}`}
      title="Since the lead came in"
    >
      {age(waited)}
    </span>
  );
}

export function UpNext({
  cockpit,
  now,
  licensedStates,
}: {
  cockpit: Cockpit;
  now: number;
  licensedStates: string[];
}) {
  const items = cockpit.upNext;
  const callable = (i: UpNextItem) => Boolean(i.phone) && mayDial(i.state, i.doNotCall, now);
  const primaryKey = items.find(callable)?.key;
  return (
    <section className="card upnext">
      <div className="card-head">
        <h3>Up next</h3>
        <span className="card-sub">Inside five minutes closes about twice as often</span>
        <Link to="/leads?status=new" className="card-link">
          {cockpit.waiting.count} new waiting
        </Link>
      </div>
      {items.length === 0 ? (
        <div className="card-empty">
          <b>Nothing waiting on you.</b> New leads and callbacks land here the moment they are due.
        </div>
      ) : (
        <ol className="upnext-list">
          {items.map((i) => {
            const can = callable(i);
            const primary = i.key === primaryKey;
            return (
              <li key={i.key} className={`upnext-row reason-${i.reason}`}>
                <span className="upnext-reason">{REASON[i.reason]}</span>
                <div className="upnext-who">
                  <Link to={href(i)} className="upnext-name">
                    {i.name}
                  </Link>
                  <span className="upnext-meta">
                    <RecordMark kind={i.taskId ? "task" : "lead"} />
                    {[i.state, i.callType, i.detail].filter(Boolean).join(" · ")}
                    {i.reason === "working_lead" &&
                      ` · ${i.attempts} ${i.attempts === 1 ? "try" : "tries"}`}
                    {i.owner === null && i.leadId && " · unassigned"}
                  </span>
                </div>
                <EligibilityFlags
                  state={i.state}
                  language={i.language}
                  consentType={i.consentType}
                  doNotCall={i.doNotCall}
                  licensedStates={licensedStates}
                  now={now}
                  compact
                />
                <Timing item={i} now={now} />
                <button
                  type="button"
                  className={`btn btn-sm${primary ? " btn-primary" : ""}`}
                  disabled={!can}
                  title={
                    !i.phone
                      ? "No number on file"
                      : can
                        ? "Opens the desk with the number filled in"
                        : "Not callable right now"
                  }
                  onClick={() => i.phone && dial(i.phone)}
                >
                  <Icon name="phoneOut" />
                  Dial
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
